import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import logo from '../assets/logo_full_black_transparent_for_header.png';
import gift_icon from '../assets/gift_icon.png';

/**
 * Header component that provides navigation and branding
 * Features responsive design with mobile menu and smooth scrolling
 */
export default function Header({ homeLink, showSiteLinks, onGiveGift, handleRedeem }) {
    // ===== State Management =====
    const [scrolled, setScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState('');
    const [isManualNavigation, setIsManualNavigation] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const manualNavigationTimer = useRef(null);

    // ===== Constants =====
    const NAVIGATION_SECTIONS = ['main-intro', 'how-it-works', 'pricing', 'samples', 'get-started'];
    const HEADER_LINKS = [
        { id: 'how-it-works', text: 'How it Works' },
        { id: 'pricing', text: 'Pricing' },
        { id: 'samples', text: 'Samples' },
        { id: 'get-started', text: 'Get Started' }
    ];
    const HEADER_LINKS_NO_SITE_LINKS = [
        { id: 'home', text: 'Home' },
    ];

    // ===== Scroll Handling =====
    const handleScroll = useCallback(() => {
        // Update scrolled state for header styling
        setScrolled(window.scrollY > 0);
        
        // Skip updating active section during manual navigation
        if (isManualNavigation) return;
        
        // Calculate current scroll position with offset
        const scrollPosition = window.scrollY + window.innerHeight / 3;
        
        // Find current active section
        let currentActiveSection = '';
        for (const section of NAVIGATION_SECTIONS) {
            const element = document.getElementById(section);
            if (!element) continue;
            
            const { top, bottom } = element.getBoundingClientRect();
            const elementTop = top + window.scrollY;
            const elementBottom = bottom + window.scrollY;
            
            if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
                // Don't set main-intro as active (it's not in the header)
                currentActiveSection = section !== 'main-intro' ? section : '';
                break;
            }
        }
        
        setActiveSection(currentActiveSection);
    }, [isManualNavigation]);

    useEffect(() => {
        // Add and remove scroll event listener
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // ===== Navigation Functions =====
    const scrollToSection = useCallback((id) => {
        const element = document.getElementById(id);
        if (!element) return;
        
        // Update UI state
        setActiveSection(id === 'main-intro' ? '' : id);
        setIsManualNavigation(true);
        setMobileMenuOpen(false);
        
        // Clear any existing navigation timer
        if (manualNavigationTimer.current) {
            clearTimeout(manualNavigationTimer.current);
        }
        
        // Calculate scroll position and duration
        const headerHeight = document.querySelector('.header-container')?.offsetHeight || 0;
        let offsetPosition = element.getBoundingClientRect().top + window.scrollY - headerHeight;
        const distance = Math.abs(window.scrollY - offsetPosition);
        const scrollDuration = Math.min(1000, 500 + distance / 2); // Cap at 1 second

        // overwrite offsetPosition for main-intro, otherwise it scrolls just a few pixels
        // short of the top of the page.
        if (id === 'main-intro') {
            offsetPosition = 0;
        }
        
        // Perform smooth scroll
        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
        
        // Re-enable scroll-based updates after scrolling completes
        manualNavigationTimer.current = setTimeout(() => {
            setIsManualNavigation(false);
        }, scrollDuration + 100);
    }, []);

    const toggleMobileMenu = useCallback(() => {
        setMobileMenuOpen(prevState => !prevState);
    }, []);

    const closeMobileMenu = useCallback(() => {
        setMobileMenuOpen(false);
    }, [setMobileMenuOpen]);

    // ===== Render Helper Functions =====
    const renderNavLink = useCallback((id, text) => (
        <a 
            key={id}
            href={`#${id}`} 
            className={`header-links ${activeSection === id ? 'active' : ''}`}
            onClick={(e) => {
                e.preventDefault();
                homeLink();
                scrollToSection(id);
            }}
        >
            {text}
            {activeSection === id && <span className="active-indicator"></span>}
        </a>
    ), [activeSection, scrollToSection]);

    const renderMobileNavLink = useCallback((id, text) => (
        <a 
            key={id}
            href={`#${id}`} 
            className={activeSection === id ? 'active' : ''}
            onClick={(e) => {
                e.preventDefault();
                homeLink();
                scrollToSection(id);
                closeMobileMenu();
            }}
        >
            {text}
        </a>
    ), [activeSection, scrollToSection, homeLink, closeMobileMenu]);

    // ===== Component Render =====
    return (
        <>
            <div className={`header-container ${scrolled ? "header-scrolled" : ""}`}>
                {/* Mobile Menu Button */}
                <button 
                    className={`mobile-menu-button`} 
                    onClick={toggleMobileMenu}
                    aria-label="Toggle navigation menu"
                    aria-expanded={mobileMenuOpen}
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
                
                {/* Logo */}
                <a 
                    href="#main-intro"
                    onClick={(e) => {
                        e.preventDefault();
                        homeLink();
                        scrollToSection('main-intro');
                    }}
                    aria-label="Go to top of page"
                >
                    <img className="header-logo" src={logo} alt="Living Legend Logo"/>
                </a>
                
                {/* Desktop Navigation Links */}
                {showSiteLinks && (
                    <div className="header-links">
                        {HEADER_LINKS.map(link => renderNavLink(link.id, link.text))}
                    </div>
                )}
                
                {/* Gift Links */}
                <div className="header-gift-container">
                    <a href="#" className="header-redeem-gift-link desktop-only" onClick={handleRedeem}>Redeem Gift</a>
                    <a href="" className="header-button" onClick={onGiveGift}>
                        <img src={gift_icon} className="header-gift-icon" alt="Gift icon"/>
                        Give as gift
                    </a>
                </div>
            </div>
            
            {/* Mobile Menu */}
            {showSiteLinks? (
                <div 
                    className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}
                    aria-hidden={!mobileMenuOpen}
                >
                    {HEADER_LINKS.map(link => renderMobileNavLink(link.id, link.text))}
                    
                    {/* Redeem Gift link in mobile menu */}
                    <a 
                        href="" 
                        className="mobile-menu-link"
                        onClick={(e) => {
                            handleRedeem(e);
                            closeMobileMenu();
                        }}
                    >
                        Redeem Gift
                    </a>
                </div>
            ) : (
                <div 
                    className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}
                    aria-hidden={!mobileMenuOpen}
                >
                    {HEADER_LINKS_NO_SITE_LINKS.map(link => renderMobileNavLink(link.id, link.text))}
                    
                    {/* Redeem Gift link in mobile menu */}
                    <a 
                        href="" 
                        className="mobile-menu-link"
                        onClick={(e) => {
                            handleRedeem(e);
                            closeMobileMenu();
                        }}
                    >
                        Redeem Gift
                    </a>
                </div>
            )}
        </>
    );
}

// PropTypes for type checking
Header.propTypes = {
    showSiteLinks: PropTypes.bool
};

// Default props
Header.defaultProps = {
    showSiteLinks: true
};